// ═══════════════════════════════════════════════════════════════
// DEBRIS — Activity Log
// Reads from activityLog table. Shows all key actions (approve,
// dispute, re-open, create, delete) with actor + timestamp.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Stack,
} from '../../components/gab/GabComponents';
import { IconHistory, IconRefresh, IconSearch } from '@tabler/icons-react';
import { readAuditLog } from '../../lib/auditLog';
import type { AuditEntry } from '../../lib/auditLog';
import { useAuthStore } from '../../store/authStore';

const MODULE_COLORS: Record<string, string> = {
  debris:    '#4B3FFF',
  reconciliation: '#3b82f6',
  invoice:   '#A627FF',
  ticket:    '#0285C4',
  forceaccount: '#7C3AED',
};

const ACTION_COLORS: Record<string, string> = {
  approved:  'var(--color-success)',
  disputed:  'var(--color-error)',
  reopened:  '#3b82f6',
  created:   '#037730',
  deleted:   '#D33423',
  updated:   '#885604',
};

function actionColor(action: string): string {
  for (const [k, v] of Object.entries(ACTION_COLORS)) {
    if (action.toLowerCase().includes(k)) return v;
  }
  return '#64748b';
}

function fmtTime(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function fmtAction(action: string): string {
  return action.replace(/\./g, ' › ').replace(/_/g, ' ');
}

export default function ActivityLog() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const data = await readAuditLog(incidentId, 500);
    setEntries(data);
    setLoading(false);
  }

  useEffect(() => { load(); }, [incidentId]);

  const modules = useMemo(() => {
    const mods = new Set(entries.map(e => e.module).filter(Boolean));
    return [
      { value: '', label: 'All modules' },
      ...[...mods].map(m => ({ value: m, label: m })),
    ];
  }, [entries]);

  const filtered = useMemo(() => {
    let list = entries;
    if (moduleFilter) list = list.filter(e => e.module === moduleFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(e =>
        e.action.toLowerCase().includes(q) ||
        e.actorName.toLowerCase().includes(q) ||
        e.actorEmail.toLowerCase().includes(q) ||
        e.targetId.toLowerCase().includes(q) ||
        e.targetType.toLowerCase().includes(q)
      );
    }
    return list;
  }, [entries, moduleFilter, search]);

  return (
    <Stack gap={16}>
      <Group justify="space-between" align="center">
        <div>
          <Title order={3} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconHistory size={22} color="#4B3FFF" /> Activity Log
          </Title>
          <Text size="sm" c="dimmed">
            Change history for reconciliations, invoices, and key debris operations.
          </Text>
        </div>
        <Button
          variant="light"
          leftSection={<IconRefresh size={14} />}
          loading={loading}
          onClick={load}
          size="xs"
        >
          Refresh
        </Button>
      </Group>

      <Group gap={8}>
        <TextInput
          placeholder="Search actions, users, IDs..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 380 }}
        />
        <Select
          data={modules}
          value={moduleFilter ?? ''}
          onChange={v => setModuleFilter(v || null)}
          style={{ width: 180 }}
          placeholder="All modules"
        />
      </Group>

      {/* Summary counts */}
      <Group gap={8}>
        <Text size="xs" c="dimmed">
          {filtered.length.toLocaleString()} event{filtered.length !== 1 ? 's' : ''}
          {entries.length !== filtered.length ? ` (filtered from ${entries.length.toLocaleString()})` : ''}
        </Text>
      </Group>

      <Paper withBorder p={0} radius="md">
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
              <tr>
                {['Time', 'Action', 'Module', 'Target', 'Actor', 'Details'].map(col => (
                  <th key={col} style={{
                    padding: '10px 12px', textAlign: 'left', fontWeight: 600,
                    color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
                  }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                  No activity recorded yet. Actions like approving reconciliations and creating invoices will appear here.
                </td></tr>
              )}
              {filtered.map((e, i) => (
                <tr key={e.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ padding: '8px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {fmtTime(e.createdAt || '')}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge
                      size="sm"
                      style={{
                        backgroundColor: `${actionColor(e.action)}18`,
                        color: actionColor(e.action),
                        border: `1px solid ${actionColor(e.action)}40`,
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      {fmtAction(e.action)}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="xs" c="dimmed">{e.module || '—'}</Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="xs">
                      {e.targetType && <span style={{ color: '#94a3b8' }}>{e.targetType} </span>}
                      {e.targetId && <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>#{e.targetId}</span>}
                      {!e.targetType && !e.targetId && '—'}
                    </Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="xs" fw={500}>{e.actorName || e.actorEmail || '—'}</Text>
                    {e.actorName && e.actorEmail && (
                      <Text size="xs" c="dimmed">{e.actorEmail}</Text>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 280 }}>
                    {Object.keys(e.details).length > 0 ? (
                      <Text size="xs" c="dimmed" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {Object.entries(e.details)
                          .filter(([, v]) => v != null && v !== '')
                          .map(([k, v]) => `${k}: ${String(v)}`)
                          .join(' · ')
                        }
                      </Text>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>
    </Stack>
  );
}
